export const EMPTY_LIVE_MESSAGE_OVERLAY = Object.freeze({});
export function liveMessageOverlayFromSnapshot(liveMessages) {
    if (liveMessages.length === 0) {
        return EMPTY_LIVE_MESSAGE_OVERLAY;
    }
    const overlay = {};
    for (const live of liveMessages) {
        overlay[live.messageId] = {
            turnId: live.turnId,
            text: live.text,
            createdAt: live.createdAt,
        };
    }
    return overlay;
}
/**
 * Apply one frame, taking only the part of it the overlay does not hold yet.
 *
 * Stale or duplicate frames (nothing beyond the held text) and frames that
 * start past the held text (a gap the frame cannot fill) leave the overlay
 * unchanged.
 */
export function applyLiveMessageFrame(overlay, frame) {
    if (frame.delta.length === 0) {
        return overlay;
    }
    const existing = overlay[frame.messageId];
    const previousLength = frame.emittedLength - frame.delta.length;
    if (existing === undefined) {
        return previousLength === 0
            ? {
                ...overlay,
                [frame.messageId]: {
                    turnId: frame.turnId,
                    text: frame.delta,
                    createdAt: frame.createdAt,
                },
            }
            : overlay;
    }
    if (frame.emittedLength <= existing.text.length || previousLength > existing.text.length) {
        return overlay;
    }
    const tail = frame.delta.slice(existing.text.length - previousLength);
    return {
        ...overlay,
        [frame.messageId]: { ...existing, text: `${existing.text}${tail}` },
    };
}
/** Forget a message's live text, once its durable text is authoritative. */
export function clearLiveMessage(overlay, messageId) {
    if (overlay[messageId] === undefined) {
        return overlay;
    }
    const { [messageId]: _removed, ...rest } = overlay;
    return rest;
}
/**
 * Ordering key for a provisional message, so it sorts after everything already
 * projected instead of ahead of it — a row with no sequence sorts before every
 * row that has one.
 *
 * A guess, and only ever a fallback: a message normally reaches the log the
 * moment its text starts, and its live text then renders onto that row with the
 * server's own key. Frames that arrive without one (the opening event still in
 * flight, or lost across a reconnect) fall back to this.
 */
export function provisionalLiveSequence(messages) {
    let maxSequence;
    for (const message of messages) {
        if (message.sequence !== undefined &&
            (maxSequence === undefined || message.sequence > maxSequence)) {
            maxSequence = message.sequence;
        }
    }
    return maxSequence === undefined ? undefined : maxSequence + 1;
}
/**
 * Live text for a message when it is ahead of the durable text, else `null`.
 *
 * Both are prefixes of the same generated text, so the longer one is the more
 * complete one.
 */
export function liveTextForMessage(overlay, message) {
    const live = overlay[message.id];
    return live !== undefined && live.text.length > message.text.length ? live.text : null;
}
/** Live text with no durable message yet; each needs a provisional row. */
export function pendingLiveMessages(overlay, messages) {
    const pending = [];
    for (const [messageId, entry] of Object.entries(overlay)) {
        if (!messages.some((message) => message.id === messageId)) {
            pending.push({ messageId, entry });
        }
    }
    return pending;
}
/**
 * Combine durable messages with live text for rendering.
 *
 * A message that exists in both keeps whichever text is longer; live text with
 * no message yet becomes a provisional streaming one.
 */
export function mergeLiveMessages(messages, overlay, provisionalSequence) {
    if (Object.keys(overlay).length === 0) {
        return messages;
    }
    const pending = pendingLiveMessages(overlay, messages);
    const merged = messages.map((message) => {
        const liveText = liveTextForMessage(overlay, message);
        return liveText === null ? message : { ...message, text: liveText };
    });
    if (pending.length === 0) {
        return merged;
    }
    const sequence = provisionalSequence ?? provisionalLiveSequence(messages);
    return [
        ...merged,
        ...pending.map(({ messageId, entry }) => ({
            id: messageId,
            role: "assistant",
            text: entry.text,
            turnId: entry.turnId,
            streaming: true,
            ...(sequence !== undefined ? { sequence } : {}),
            createdAt: entry.createdAt,
            updatedAt: entry.createdAt,
        })),
    ];
}
